import React, { useState } from "react";
import { func } from "prop-types";
import { Formik, Form, Field } from "formik";
import { Row, Col, Collapse } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";

import { Input, FieldSpy } from "../../../../../components/formik";
import {
  CLIENT_TYPE,
  FILTERS_LABELS_SINGLE_CLIENT_REPORT,
  INITIAL_FILTERS_SINGLE_CLIENT_REPORT,
} from "../../../constants/singleClientEntriesReportConstants";

import { ButtonWithSpinner } from "../../../../../components/common";

import {
  singleClientReportIsLoadingSelector,
  singleClientReportFiltersSelector,
} from "../../../selectors";
import {
  clearSingleClientReportEntries,
  getSingleClientReportEntriesCount,
  setSingleClientReportEntriesSearchFilters,
} from "../../../actions";

import { singleClientReportNoneSelectedValidationSchema } from "../../../validation-schemas";
import { withError } from "../../../../../components/hocs";
import "./singleClientReportForm.css";
import Clickable from "../../../../../components/common/Clickable";
import { CollapseButton, CollapseOpenIcon } from "../../../../../assets";
import { FaTimes } from "react-icons/fa";
import { useRef } from "react";
import {
  getFiltersValues,
  shouldShowFiltersLabels,
} from "../../../../../utilities/filters";
import ButtonSelectSingleClientReport from "../../../../../components/formik/single-client-report/ButtonSelectSingleClientReport";

const ErrorInput = withError(Input);

const SingleClientReportForm = ({
  handleSubmit,
  clientTypes,
  toggleCollapse,
  isOpenCollapse,
  singleClientReportEntries,
  setIsSubmitted,
  organizationInfo,
}) => {
  const [clientType, setClientType] = useState(null);
  const [key, setKey] = useState(clientType);
  const formikRef = useRef(null);

  const [rerender, setReRender] = useState(false);

  const dispatch = useDispatch();

  const filters = useSelector(singleClientReportFiltersSelector);

  const resetInitialValues = (type) => {
    let cleanCustom = {};

    if (filters.customClientParameters) {
      Object.values(filters.customClientParameters).forEach((l) => {
        l["value"] = undefined;
        cleanCustom[l.name] = undefined;
      });
    }

    return {
      ...INITIAL_FILTERS_SINGLE_CLIENT_REPORT,
      ...cleanCustom,
      type,
    };
  };

  const isLoading = useSelector(singleClientReportIsLoadingSelector);

  const handleClientTypeChange = (clientTypeValue) => {
    const category = clientTypes.filter((t) => t.value === clientTypeValue)[0];

    if (category) {
      setClientType(category.category);
    }

    dispatch(
      setSingleClientReportEntriesSearchFilters(
        resetInitialValues(clientTypeValue)
      )
    );
    setKey(clientTypeValue);
  };

  const hasAdditionalProperties = () => {
    return (
      organizationInfo && organizationInfo.additionalOrganizationProperties
    );
  };

  const changeCustomClientParameter = (props, index, element, e) => {
    if (!props.customClientParameters) {
      props.customClientParameters = [];
    }
    if (!props.customClientParameters[index]) {
      props.customClientParameters[index] = {};
    }
    if (!e) {
      const temp = [...props.customClientParameters];
      const withRemovedEmptyValue = temp.filter((p) => p.name !== element.name);
      props.customClientParameters = withRemovedEmptyValue;
    } else {
      props.customClientParameters[index].name = element.name;
      props.customClientParameters[index].value = e;
      formikRef.current.setFieldValue(element.name, e || null);
    }
  };

  if (rerender) {
    // too many dynamic changes - ugly but easy way to clear what we don't need from prev. render
    setTimeout(() => {
      setReRender(false);
    });
    return null;
  }

  return (
    <Formik
      ref={formikRef}
      onSubmit={handleSubmit}
      initialValues={filters}
      key={key}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={singleClientReportNoneSelectedValidationSchema}
    >
      {(props) => {
        const { errors } = props;
        return (
          <div>
            <div className="collapse-search-single-client-report">
              <Collapse isOpen={isOpenCollapse}>
                <Form className="single-client-report-search">
                  <div className="single-client-report-search-inner-box">
                    <div>
                      <Field
                        type="text"
                        name="type"
                        placeholder="Client Type"
                        component={ButtonSelectSingleClientReport}
                        className="dropdown-group"
                        options={clientTypes}
                      />
                      <FieldSpy name="type" onChange={handleClientTypeChange} />
                    </div>
                    {clientType === CLIENT_TYPE.PERSON ? (
                      <React.Fragment>
                        <div>
                          <Field
                            type="text"
                            name="firstName"
                            placeholder="First Name"
                            component={ErrorInput}
                          />
                        </div>
                        <div>
                          <Field
                            type="text"
                            name="middleName"
                            placeholder="Middle Name"
                            component={ErrorInput}
                          />
                        </div>
                        <div>
                          <Field
                            type="text"
                            name="lastName"
                            placeholder="Last Name"
                            component={ErrorInput}
                          />
                        </div>
                      </React.Fragment>
                    ) : null}
                    <div>
                      <Field
                        type="text"
                        name="clientNumber"
                        placeholder={"Client EGN / Company number"}
                        component={Input}
                      />
                    </div>
                    <div>
                      <Field
                        type="text"
                        name="clientOfferId"
                        placeholder="Offer ID"
                        component={Input}
                      />
                    </div>
                    {clientType === CLIENT_TYPE.COMPANY ? (
                      <React.Fragment>
                        <div>
                          <Field
                            type="text"
                            name="fullName"
                            placeholder="Company Name"
                            component={ErrorInput}
                          />
                        </div>
                      </React.Fragment>
                    ) : null}
                    {hasAdditionalProperties()
                      ? organizationInfo.additionalOrganizationProperties.map(
                          (element, index) => {
                            return (
                              <div>
                                <Field
                                  key={`${index}-${element.name}`}
                                  type="text"
                                  name={element.name}
                                  placeholder={element.placeholder}
                                  component={Input}
                                />
                                <FieldSpy
                                  key={index}
                                  name={element.name}
                                  onChange={(e) =>
                                    changeCustomClientParameter(
                                      props.values,
                                      index,
                                      element,
                                      e.trim()
                                    )
                                  }
                                />
                              </div>
                            );
                          }
                        )
                      : null}{" "}
                  </div>
                  <Row>
                    <Col lg={12}>
                      {errors.atLeastOneField && (
                        <React.Fragment>
                          <div className="form-error-message center">
                            {errors.atLeastOneField}
                          </div>
                        </React.Fragment>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={12} className="d-flex justify-content-center pt-4">
                      <ButtonWithSpinner
                        id="searchMonitoringQueueEntriesSubmitButton"
                        text="Search"
                        outline
                        type="submit"
                        className="search-button-single-client-report"
                        loading={isLoading}
                      />
                    </Col>
                  </Row>
                </Form>
                <div className="filters-menu-collapse-button-single-client-report ">
                  {singleClientReportEntries.length ? (
                    <Clickable onClick={toggleCollapse}>
                      <CollapseButton />
                    </Clickable>
                  ) : null}
                </div>
              </Collapse>
            </div>
            <div className="filters-menu-collapse-button-single-client-report ">
              <Clickable onClick={toggleCollapse}>
                {!isOpenCollapse ? <CollapseOpenIcon /> : null}
              </Clickable>
            </div>
            <div className="filters-main-container-single-client-report">
              <div className="single-filters-container-single-client-report">
                {shouldShowFiltersLabels(filters) ? (
                  <div className="filters-menu-label">Filter Applied</div>
                ) : null}
                {getFiltersValues(filters).map(([key, value], i) => {
                  return value ? (
                    <SingleFilter
                      key={key}
                      filterKey={key}
                      filterValue={value}
                      clearFilter={() => clearSingleFilter(key)}
                    />
                  ) : null;
                })}
                {shouldShowFiltersLabels(filters) ? (
                  <div
                    onClick={clearFilters}
                    className="filters-menu-label-clear"
                  >
                    Clear All
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );

  function clearFilters() {
    dispatch(clearSingleClientReportEntries());

    setClientType(null);

    formikRef.current.resetForm({
      ...INITIAL_FILTERS_SINGLE_CLIENT_REPORT,
    });

    setReRender(true);

    toggleCollapse();
  }

  function clearSingleFilter(filterKey) {
    let val = "";
    const currentFilters = { ...filters };

    dispatch(
      setSingleClientReportEntriesSearchFilters({
        [filterKey]: val,
      })
    );

    if (currentFilters.customClientParameters) {
      const filterPresent = currentFilters.customClientParameters.some(
        (f) => f.name === filterKey
      );
      if (filterPresent) {
        const customParametersClean =
          currentFilters.customClientParameters.filter(
            (parameter) => parameter.name !== filterKey
          );

        currentFilters["customClientParameters"] = customParametersClean;
        val = null;
      }
    }

    currentFilters[filterKey] = val;
    formikRef.current.setFieldValue(filterKey, val);
    dispatch(
      setSingleClientReportEntriesSearchFilters({
        ...currentFilters,
      })
    );
    dispatch(getSingleClientReportEntriesCount(currentFilters));
    setIsSubmitted(true);
    setReRender(true);
  }

  function SingleFilter({
    filterKey = "",
    filterValue = "",
    clearFilter = () => {},
  }) {
    let currentValue = filterValue;

    if (filterKey === "customClientParameters") {
      return null;
    }

    if (filterKey === "type" && filterValue) {
      currentValue = filterValue;
    }

    return (
      <div className="single-filter-box">
        <div className="single-filter-label">
          {FILTERS_LABELS_SINGLE_CLIENT_REPORT[filterKey] || `${filterKey}:`}
        </div>
        <div className="single-filter-chip">
          {currentValue}
          {filterKey !== "type" &&
          Object.values(filters).filter(Boolean).length > 2 ? (
            <FaTimes
              className="remove-filter-icon"
              onClick={clearFilter}
              color="#707070"
            />
          ) : null}
        </div>
      </div>
    );
  }
};

SingleClientReportForm.propTypes = {
  handleSubmit: func.isRequired,
};

export default SingleClientReportForm;



// WEBPACK FOOTER //
// ./src/features/reports/components/single-client/form/SingleClientReportForm.jsx